import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { ScrollArea } from "@/components/ui/scroll-area";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Euro,
  Users,
  Factory,
  CheckSquare,
  BarChart3,
  TrendingUp,
  Package,
  UserCheck,
  Blocks,
  Loader2,
  Search,
  X,
} from "lucide-react";
import React, { useContext, useState, useEffect } from "react";
import { useTranslation } from "react-i18next";
import AuthContext from "@/store/contexts/AuthContext";
import axios from "@/services/axios";
import {
  fetchPublishedTemplates,
  fetchTemplateCategories,
  type OdashTemplate,
  type TemplateCategory,
} from "@/services/templatesApi";
import { Input } from "@/components/ui/input";

// Icon mapping helper
const getIconForCategory = (iconName: string) => {
  const iconMap: { [key: string]: React.ReactNode } = {
    "📊": <BarChart3 className="w-8 h-8" />,
    "💰": <Euro className="w-8 h-8" />,
    "👥": <Users className="w-8 h-8" />,
    "🏭": <Factory className="w-8 h-8" />,
    "📋": <CheckSquare className="w-8 h-8" />,
    "📦": <Package className="w-8 h-8" />,
    "🎧": <UserCheck className="w-8 h-8" />,
    "🎯": <TrendingUp className="w-8 h-8" />,
  };
  return iconMap[iconName] || <Blocks className="w-8 h-8" />;
};

const getCategoryColor = (index: number) => {
  const colors = [
    "bg-green-100 text-green-700",
    "bg-blue-100 text-blue-700",
    "bg-orange-100 text-orange-700",
    "bg-purple-100 text-purple-700",
    "bg-indigo-100 text-indigo-700",
    "bg-pink-100 text-pink-700",
    "bg-yellow-100 text-yellow-700",
    "bg-red-100 text-red-700",
  ];
  return colors[index % colors.length];
};

const BlockTemplateDialog = ({
  id,
  blockType,
  children,
}: {
  id: string;
  blockType: "data" | "graph" | "table";
  children: React.ReactNode;
}) => {
  const { loadConfigs } = useContext(AuthContext);
  const { t } = useTranslation();
  const [open, setOpen] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState<OdashTemplate[]>([]);
  const [categories, setCategories] = useState<TemplateCategory[]>([]);
  const [blockTemplateCategories, setBlockTemplateCategories] = useState<any[]>([]);
  const [searchQuery, setSearchQuery] = useState("");

  // Load templates and categories when dialog opens, reset search when closed
  useEffect(() => {
    if (open) {
      loadTemplatesData();
    } else {
      setSearchQuery("");
      setSelectedTemplate(null);
    }
  }, [open]);

  const loadTemplatesData = async () => {
    setLoading(true);
    try {
      const [templatesData, categoriesData] = await Promise.all([
        fetchPublishedTemplates(),
        fetchTemplateCategories(),
      ]);

      setTemplates(templatesData);
      setCategories(categoriesData);

      // Transform data for UI
      const transformedCategories = categoriesData.map((category, index) => {
        // Get templates for this category that match the requested blockType
        const categoryTemplates = templatesData
          .filter((template) => {
            if (!template.category || template.category.id !== category.id) {
              return false;
            }
            // Only include component_library templates
            if (template.template_type !== "component_library") {
              return false;
            }
            // Check if template's component_type matches the requested blockType
            return template.component_type === blockType;
          })
          .map((template) => {
            // Get all block configs from the template
            const blockConfigs = template.template_data?.configs || [];
            
            // Transform each block config into a template item
            return blockConfigs.map((blockConfig: any) => ({
              id: blockConfig.config_id || `${template.id}_${blockConfig.config?.id}`,
              name: blockConfig.name || blockConfig.config?.title || "Unnamed Block",
              description: template.short_description || blockConfig.config?.description || "",
              icon: <Blocks className="w-6 h-6" />,
              config: blockConfig,
              blockType: blockType,
              tags: template.tags.map((t) => t.name),
              templateId: template.id,
            }));
          })
          .flat();

        return {
          id: category.id.toString(),
          categoryName: category.name,
          categoryDescription: category.description,
          icon: getIconForCategory(category.icon),
          color: getCategoryColor(index),
          templates: categoryTemplates,
        };
      }).filter((cat) => cat.templates.length > 0); // Only include categories with templates

      setBlockTemplateCategories(transformedCategories);
    } catch (error) {
      console.error("Error loading templates:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleTemplateSelect = (templateId: string) => {
    setSelectedTemplate(templateId);
  };

  // Filter categories and templates based on search query
  const filterCategoriesAndTemplates = (categories: any[], query: string) => {
    if (!query.trim()) {
      return categories;
    }

    const lowerQuery = query.toLowerCase();

    return categories
      .map((category) => {
        // Check if category name matches
        const categoryMatches = category.categoryName.toLowerCase().includes(lowerQuery);

        // Filter templates within this category
        const filteredTemplates = category.templates.filter((template: any) => {
          // If category matches, include all templates in this category
          if (categoryMatches) {
            return true;
          }

          // Otherwise, check if template matches
          // Search in template name
          if (template.name.toLowerCase().includes(lowerQuery)) {
            return true;
          }

          // Search in description
          if (template.description?.toLowerCase().includes(lowerQuery)) {
            return true;
          }

          // Search in tags
          if (template.tags?.some((tag: string) => tag.toLowerCase().includes(lowerQuery))) {
            return true;
          }

          return false;
        });

        return {
          ...category,
          templates: filteredTemplates,
        };
      })
      .filter((category) => category.templates.length > 0); // Hide empty categories
  };

  const filteredCategories = filterCategoriesAndTemplates(blockTemplateCategories, searchQuery);

  const handleUseTemplate = async () => {
    if (selectedTemplate) {
      // Find the template across all categories
      let template = null;
      for (const category of blockTemplateCategories) {
        const found = category.templates.find((tmpl: any) => tmpl.id === selectedTemplate);
        if (found) {
          template = found;
          break;
        }
      }

      if (template) {
        const exist = await axios
          .get(`/odash/data/${id}`)
          .then((e) => e.data)
          .catch(() => null);

        let configStr = JSON.stringify(template.config.config);

        configStr = configStr.replace(
          new RegExp(template.config.config.id, "g"),
          id
        );

        const updatedConfig = exist
          ? await axios
              .put(`/odash/data/${id}`, JSON.parse(configStr))
              .then((e) => e.data)
              .catch(() => null)
          : await axios
              .post(`/odash/data`, JSON.parse(configStr))
              .then((e) => e.data)
              .catch(() => null);

        if (updatedConfig) {
          await loadConfigs();

          setOpen(false);
          setSelectedTemplate(null);
        }
      }
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>{children}</DialogTrigger>
      <DialogContent className="!max-w-5xl">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Blocks className="w-5 h-5" />
            {t(`home.blockTemplateDialog.title.${blockType}`)}
          </DialogTitle>
          <DialogDescription>
            {t(`home.blockTemplateDialog.description.${blockType}`)}
          </DialogDescription>
        </DialogHeader>

        {/* Search Bar */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            type="text"
            placeholder={t("home.blockTemplateDialog.searchPlaceholder") || "Search templates, categories, or tags..."}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-9 pr-9"
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery("")}
              className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground transition-colors"
            >
              <X className="w-4 h-4" />
            </button>
          )}
        </div>

        <ScrollArea className="h-[50vh]">
          <div className="p-2 pr-4">
            {loading ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-primary mb-4" />
                <p className="text-muted-foreground">{t("home.blockTemplateDialog.loadingTemplates")}</p>
              </div>
            ) : filteredCategories.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-12">
                <Blocks className="w-12 h-12 text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground">
                  {searchQuery
                    ? t("home.blockTemplateDialog.noMatchingTemplates") || `No templates found matching "${searchQuery}"`
                    : t("home.blockTemplateDialog.noTemplatesAvailable")}
                </p>
                <p className="text-sm text-muted-foreground/70">
                  {searchQuery
                    ? t("home.blockTemplateDialog.tryDifferentSearch") || "Try a different search term"
                    : t(`home.blockTemplateDialog.checkBackLater.${blockType}`)}
                </p>
              </div>
            ) : (
              // All Templates Grouped by Category
              <div className="space-y-6">
                {filteredCategories.map((category) => (
                  <div key={category.id}>
                    {/* Category Header */}
                    <div className="flex items-center gap-3 mb-3 pb-2 border-b">
                      <div className={`p-2 rounded-lg ${category.color}`}>
                        {category.icon}
                      </div>
                      <div className="flex-1">
                        <h3 className="font-semibold text-lg">{category.categoryName}</h3>
                        <p className="text-sm text-muted-foreground">{category.categoryDescription}</p>
                      </div>
                      <Badge variant="secondary">
                        {category.templates.length} {category.templates.length === 1 ? t("home.blockTemplateDialog.template") : t("home.blockTemplateDialog.templates")}
                      </Badge>
                    </div>

                    {/* Templates in this Category */}
                    <div className="grid grid-cols-1 gap-3">
                      {category.templates.map((template: any) => (
                        <Card
                          key={template.id}
                          className={`cursor-pointer transition-all hover:shadow-md ${
                            selectedTemplate === template.id
                              ? "ring-2 ring-primary border-primary"
                              : "hover:border-muted-foreground/50"
                          }`}
                          onClick={() => handleTemplateSelect(template.id)}
                        >
                          <CardHeader className="pb-3">
                            <div className="flex items-start justify-between">
                              <div className="flex items-center gap-3">
                                <div className="p-2 rounded-lg bg-primary/10 text-primary">
                                  {template.icon}
                                </div>
                                <div>
                                  <CardTitle className="text-base">
                                    {template.name}
                                  </CardTitle>
                                  <div className="flex gap-1 mt-1">
                                    {template.tags.map((tag: string) => (
                                      <Badge
                                        key={tag}
                                        variant="outline"
                                        className="text-xs"
                                      >
                                        {tag}
                                      </Badge>
                                    ))}
                                  </div>
                                </div>
                              </div>
                              {selectedTemplate === template.id && (
                                <div className="w-5 h-5 rounded-full bg-primary flex items-center justify-center">
                                  <div className="w-2 h-2 rounded-full bg-white" />
                                </div>
                              )}
                            </div>
                          </CardHeader>
                          <CardContent>
                            <CardDescription className="text-sm">
                              {template.description}
                            </CardDescription>
                          </CardContent>
                        </Card>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </ScrollArea>

        <DialogFooter className="mt-4">
          <DialogClose asChild>
            <Button variant="outline">{t("home.blockTemplateDialog.cancel")}</Button>
          </DialogClose>
          <Button disabled={!selectedTemplate} onClick={handleUseTemplate}>
            {t(`home.blockTemplateDialog.addBlock.${blockType}`)}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default BlockTemplateDialog;
